import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrainCircuit, RefreshCw } from 'lucide-react';
import { Asset } from '../types';
import { fetchStockAdvice } from '../services/stockAdviceService';
import { StockAdviceReport } from '../types/stockAdvice';
import { StockScoreHero } from './StockScoreHero';
import { ScoreTile } from './ScoreTile';
import { MetricScoreCard } from './MetricScoreCard';
import { TradePlanCard } from './TradePlanCard';
import { CatalystCard } from './CatalystCard';
import { RiskCard } from './RiskCard';
import { sanitizeUserWarning } from './stockAdviceFormatting';

interface StockAdvicePanelProps {
  asset: Asset;
  isOpen: boolean;
}

const FALLBACK_REPORT: StockAdviceReport = {
  symbol: 'N/A', name: 'Unknown', exchange: 'N/A', asOf: new Date().toISOString(), generatedAt: new Date().toISOString(), confidence: 'Low',
  overallScore: 50, investmentStyle: 'Balanced', headline: 'Data unavailable', summary: 'Data unavailable',
  scoreBreakdown: { value: 50, quality: 50, momentum: 50, financialStrength: 50, earningsQuality: 50, moat: 50, risk: 50 },
  fundamentals: [], moat: [], quant: [], riskFactors: [], catalysts: [], verificationNotes: [], disclaimer: 'Educational analysis only. Not investment advice.',
  technicalSummary: 'Data unavailable', companySnapshot: 'Data unavailable',
  tradePlan: { bias: 'Neutral', timeframe: 'Data unavailable', entryZone: 'Data unavailable', stopLoss: 'Data unavailable', target1: 'Data unavailable', riskReward: 'Data unavailable', invalidation: 'Data unavailable' },
};

export const StockAdvicePanel: React.FC<StockAdvicePanelProps> = ({ asset, isOpen }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<StockAdviceReport | null>(null);
  const [cached, setCached] = useState(false);
  const lastAutoLoadKey = useRef<string | null>(null);

  const stockIdentityKey = useMemo(
    () => [asset.instrumentId, asset.symbol, asset.exchange, asset.isin, asset.name].join('::'),
    [asset.exchange, asset.instrumentId, asset.isin, asset.name, asset.symbol],
  );

  const loadAdvice = useCallback(async () => {
    if (!isOpen || asset.type !== 'Stock' || loading) return;
    const symbol = String(asset.symbol || '').trim();
    if (!symbol) {
      setError('Stock symbol is required before generating AI advice.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetchStockAdvice({
        assetType: 'Stock',
        instrumentId: asset.instrumentId,
        symbol,
        name: asset.name,
        exchange: asset.exchange,
        isin: asset.isin,
      });
      setCached(response.cached);
      setReport(response.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load scorecard.');
    } finally {
      setLoading(false);
    }
  }, [asset.exchange, asset.instrumentId, asset.isin, asset.name, asset.symbol, asset.type, isOpen, loading]);

  useEffect(() => {
    if (!isOpen || asset.type !== 'Stock') return;
    if (lastAutoLoadKey.current === stockIdentityKey) return;
    lastAutoLoadKey.current = stockIdentityKey;
    void loadAdvice();
  }, [asset.type, isOpen, loadAdvice, stockIdentityKey]);

  const safeReport = useMemo(() => ({ ...FALLBACK_REPORT, ...report }), [report]);
  const warnings = (safeReport.warnings || []).map(sanitizeUserWarning).filter((w): w is string => Boolean(w));

  const factorTiles = [
    ['Value', safeReport.scoreBreakdown.value],
    ['Quality', safeReport.scoreBreakdown.quality],
    ['Momentum', safeReport.scoreBreakdown.momentum],
    ['Financial Strength', safeReport.scoreBreakdown.financialStrength],
    ['Earnings Quality', safeReport.scoreBreakdown.earningsQuality],
    ['Moat', safeReport.scoreBreakdown.moat],
    ['Risk', safeReport.scoreBreakdown.risk],
  ] as const;

  const withCoverageNote = useCallback((metrics: StockAdviceReport['fundamentals']) => {
    const genericReasonCount = metrics.filter((metric) => /limited coverage from available provider data\.?/i.test(metric.reason.trim())).length;
    return genericReasonCount >= 2 ? 'Coverage note: provider data is partial for this section.' : undefined;
  }, []);

  return (
    <div className="mt-6 rounded-3xl border border-white/10 bg-[#0f1018] p-4 md:p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BrainCircuit size={16} className="text-indigo-300" />
          <p className="text-sm text-slate-200">AI Hedge Fund Scorecard</p>
        </div>
        <button onClick={() => void loadAdvice()} disabled={loading} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-60">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading ? <p className="text-sm text-slate-300">Building quant scorecard...</p> : null}
      {error ? <p className="text-sm text-rose-300">{error}</p> : null}
      {!loading && !error && !report ? <p className="text-sm text-slate-400">No scorecard available yet.</p> : null}

      {!loading && !error && report ? (
        <>
          <StockScoreHero report={safeReport} cached={cached} />

          <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {factorTiles.map(([title, score]) => <ScoreTile key={title} title={title} score={score} />)}
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <MetricScoreCard title="Fundamentals" metrics={safeReport.fundamentals} exchange={safeReport.exchange} coverageNote={withCoverageNote(safeReport.fundamentals)} />
            <MetricScoreCard title="Moat" metrics={safeReport.moat} exchange={safeReport.exchange} coverageNote={withCoverageNote(safeReport.moat)} />
            <MetricScoreCard title="Quant / Setup" metrics={safeReport.quant} exchange={safeReport.exchange} coverageNote={withCoverageNote(safeReport.quant)} />
          </section>

          <TradePlanCard tradePlan={safeReport.tradePlan} exchange={safeReport.exchange} />
          <CatalystCard
            nextEarningsDate={safeReport.nextEarningsDate}
            lastEarningsDate={safeReport.lastEarningsDate}
            earningsTrendSummary={safeReport.earningsTrendSummary}
            catalysts={safeReport.catalysts}
            exchange={safeReport.exchange}
          />
          <RiskCard riskFactors={safeReport.riskFactors} warnings={warnings} verificationNotes={safeReport.verificationNotes} />
          <section className="rounded-2xl border border-white/10 bg-[#13131b] p-3">
            <p className="text-xs text-slate-500">{safeReport.disclaimer}</p>
          </section>
        </>
      ) : null}
    </div>
  );
};
